import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, GraduationCap, CheckCircle2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/BrandIcons";

/**
 * Public enrolment enquiry page. Guests submit ward details; message is stored
 * as a contact_message with subject="Enrolment enquiry" so admin sees it in the
 * inbox. A prominent WhatsApp button is provided for instant contact.
 */
export default function EnrolWard() {
  const [copy, setCopy] = useState({});
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    ward_name: "", ward_age: "", sport_interest: "",
    preferred_batch: "", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/homepage").then((r) => setCopy(r.data || {})).catch(() => {});
  }, []);

  const c = copy.contact || {};
  const s = c.socials || {};

  // Pre-composed WhatsApp deep-link with a greeting the parent can edit
  const whatsappHref = s.whatsapp
    ? (s.whatsapp.startsWith("http")
        ? s.whatsapp
        : `https://wa.me/${s.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi HTP Sports Hub, I'd like to enrol my ward. Please share the next steps.")}`
      )
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const message = [
        `Ward: ${form.ward_name} (age ${form.ward_age || "—"})`,
        `Sport of interest: ${form.sport_interest || "—"}`,
        `Preferred batch: ${form.preferred_batch || "—"}`,
        form.notes ? `Notes: ${form.notes}` : null,
      ].filter(Boolean).join("\n");
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: "Enrolment enquiry",
        message,
      });
      setSubmitted(true);
      toast.success("Enrolment enquiry received — we'll call you within 24 hours");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="enrol-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Academy · Enrolment</div>
      <h1 className="font-display text-5xl md:text-7xl font-black uppercase leading-none tracking-tighter">
        Enrol your<br /><span className="brand-blue">ward.</span>
      </h1>
      <p className="text-white/60 mt-6 max-w-2xl">
        Fill in the form below with your child&apos;s details. Our academy manager will call you within 24 hours to schedule a trial session and confirm the batch. Prefer to chat? Message us on WhatsApp — we usually reply in a few minutes.
      </p>

      {/* Quick contact strip */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="enrol-contact-strip">
        {whatsappHref && (
          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="enrol-whatsapp"
            className="flex items-center justify-center gap-3 bg-[#25D366] text-black py-4 font-bold text-sm uppercase tracking-widest hover:opacity-90"
          >
            <WhatsAppIcon size={18} /> Chat on WhatsApp
          </a>
        )}
        {c.phone && (
          <a
            href={`tel:${(c.phone || "").replace(/[^+0-9]/g, "")}`}
            data-testid="enrol-call"
            className="flex items-center justify-center gap-3 border border-brand-blue text-brand-blue py-4 font-bold text-sm uppercase tracking-widest hover:bg-brand-blue hover:text-white transition"
          >
            <Phone size={16} /> {c.phone}
          </a>
        )}
        {c.email && (
          <a
            href={`mailto:${c.email}?subject=Enrolment%20enquiry`}
            data-testid="enrol-email"
            className="flex items-center justify-center gap-3 border border-brand-orange text-brand-orange py-4 font-bold text-sm uppercase tracking-widest hover:bg-brand-orange hover:text-black transition"
          >
            <Mail size={16} /> Email us
          </a>
        )}
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: enrolment form */}
        <div className="lg:col-span-2">
          {submitted ? (
            <div className="border border-brand-blue bg-brand-blue/5 p-10 text-center" data-testid="enrol-thanks">
              <CheckCircle2 className="brand-blue mx-auto" size={48} />
              <h2 className="font-display text-3xl uppercase font-black mt-4">Enquiry received!</h2>
              <p className="text-white/60 mt-3 max-w-md mx-auto">
                Thanks, {form.name.split(" ")[0]}. Our academy manager will call <span className="brand-blue">{form.phone}</span> within 24 hours to schedule a trial session for <b>{form.ward_name}</b>.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", ward_name: "", ward_age: "", sport_interest: "", preferred_batch: "", notes: "" }); }}
                className="mt-6 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/12 bg-[#131A2B] p-6 md:p-8 space-y-5" data-testid="enrol-form">
              <div className="font-mono text-xs uppercase tracking-widest brand-blue flex items-center gap-2">
                <GraduationCap size={14} /> Enrolment details
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Parent / Guardian name*" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="enrol-name" />
                <Input label="Phone*" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="enrol-phone" />
                <Input label="Email*" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="enrol-email-input" />
                <div />
                <Input label="Ward name*" value={form.ward_name} onChange={(v) => setForm({ ...form, ward_name: v })} required testid="enrol-ward-name" />
                <Input label="Ward age*" type="number" value={form.ward_age} onChange={(v) => setForm({ ...form, ward_age: v })} required testid="enrol-ward-age" />
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 block mb-1">Sport of interest*</label>
                  <select
                    value={form.sport_interest}
                    onChange={(e) => setForm({ ...form, sport_interest: e.target.value })}
                    required
                    data-testid="enrol-sport"
                    className="w-full bg-transparent border border-white/20 py-3 px-3 text-sm"
                  >
                    <option value="" className="bg-[#131A2B]">— Select —</option>
                    <option value="cricket" className="bg-[#131A2B]">Cricket</option>
                    <option value="football" className="bg-[#131A2B]">Football</option>
                    <option value="badminton" className="bg-[#131A2B]">Badminton</option>
                    <option value="swimming" className="bg-[#131A2B]">Swimming</option>
                    <option value="other" className="bg-[#131A2B]">Other</option>
                  </select>
                </div>
                <Input label="Preferred batch / time (optional)" value={form.preferred_batch} onChange={(v) => setForm({ ...form, preferred_batch: v })} testid="enrol-batch" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-white/50 block mb-1">Anything else we should know?</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="enrol-notes"
                  className="w-full bg-transparent border border-white/20 py-3 px-3 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="enrol-submit"
                className="w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50"
              >
                {loading ? "Sending…" : "Submit Enrolment Enquiry"}
              </button>
              <p className="text-[10px] text-white/40 font-mono">
                We&apos;ll never spam. Your details go straight to our academy manager.
              </p>
            </form>
          )}
        </div>

        {/* Right: sidebar with facility info + address */}
        <aside className="space-y-4">
          <div className="border border-white/12 bg-[#131A2B] p-6">
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">// What happens next</div>
            <ol className="space-y-3 text-sm text-white/70">
              <li className="flex gap-3">
                <span className="font-display text-2xl font-black brand-blue leading-none">01</span>
                <span>You submit this form or WhatsApp us.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-2xl font-black brand-blue leading-none">02</span>
                <span>Academy manager calls within 24 hours.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-2xl font-black brand-blue leading-none">03</span>
                <span>Free trial session scheduled at your convenience.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-2xl font-black brand-blue leading-none">04</span>
                <span>Enrol into the batch that suits your ward. Fee options: monthly, quarterly, or custom.</span>
              </li>
            </ol>
          </div>

          <div className="border border-white/12 bg-[#131A2B] p-6">
            <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">// Visit us</div>
            <div className="space-y-3 text-sm text-white/70">
              <div className="flex items-start gap-3">
                <MapPin size={14} className="brand-orange mt-1 shrink-0" />
                <span>{c.address || "HTP Sports Hub — Sector 42, Sports District"}</span>
              </div>
              {c.phone && (
                <a href={`tel:${c.phone}`} className="flex items-center gap-3 hover:brand-blue">
                  <Phone size={14} className="brand-orange" /> {c.phone}
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} className="flex items-center gap-3 hover:brand-blue">
                  <Mail size={14} className="brand-orange" /> {c.email}
                </a>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-white/50 block mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-white/20 py-3 px-3 text-sm focus:outline-none focus:border-brand-blue"
      />
    </label>
  );
}
